import React from 'react';
import { Download, Lock, Clock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { AggregatedClientDocument } from '../../types/aggregated-documents';
import { aggregatedDocumentsService } from '../../services/aggregated-documents-service';
import { format, startOfDay, isSameDay, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDbTimestamp } from '../../utils/datetime';
import { DocumentDescription } from './DocumentDescription';

interface TimelineDocumentsViewProps {
  documents: AggregatedClientDocument[];
  selectedDocs: Set<string>;
  onToggleSelect: (docId: string) => void;
  onDownload: (doc: AggregatedClientDocument) => void;
  onPreview: (doc: AggregatedClientDocument) => void;
  /** Grava a finalidade do documento (null apaga); sem ela, só exibe. */
  getDescriptionSaver?: (doc: AggregatedClientDocument) => ((description: string | null) => Promise<void>) | undefined;
}

export const TimelineDocumentsView: React.FC<TimelineDocumentsViewProps> = ({
  documents,
  selectedDocs,
  onToggleSelect,
  onDownload,
  onPreview,
  getDescriptionSaver
}) => {
  // Group documents by date
  const groupedByDate = documents.reduce((groups, doc) => {
    const uploadedAt = parseDbTimestamp(doc.uploadedAt);
    if (!uploadedAt) return groups;
    const dateKey = startOfDay(uploadedAt).toISOString();
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(doc);
    return groups;
  }, {} as Record<string, AggregatedClientDocument[]>);

  // Sort dates descending
  const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
    new Date(b).getTime() - new Date(a).getTime()
  );

  const getDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = subDays(today, 1);

    if (isSameDay(date, today)) {
      return 'Hoje';
    }
    if (isSameDay(date, yesterday)) {
      return 'Ontem';
    }
    return format(date, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  if (documents.length === 0) {
    return (
      <div className="rounded-lg border bg-muted/40 py-12 text-center">
        <Clock className="mx-auto mb-4 size-10 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Nenhum documento encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {sortedDates.map((dateKey) => {
        const docs = groupedByDate[dateKey];
        const dateLabel = getDateLabel(dateKey);

        return (
          <div key={dateKey} className="relative">
            {/* Date header */}
            <div className="sticky top-0 z-10 bg-background pb-4">
              <div className="flex items-center">
                <div className="flex-1 border-t"></div>
                <div className="rounded-full border bg-card px-3 py-1.5 text-sm font-semibold sm:px-4">
                  <Clock className="mr-2 inline size-4 text-muted-foreground" aria-hidden="true" />
                  {dateLabel}
                  <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">
                    {docs.length}
                  </span>
                </div>
                <div className="flex-1 border-t"></div>
              </div>
            </div>

            {/* Documents for this date */}
            <div className="ml-2 space-y-4 border-l-2 pl-5 sm:ml-4 sm:pl-8">
              {docs.map((doc) => {
                const isSelected = selectedDocs.has(doc.id);
                const icon = aggregatedDocumentsService.getFileIcon(doc.mimeType);
                const size = aggregatedDocumentsService.formatFileSize(doc.fileSize);
                const sourceIcon = aggregatedDocumentsService.getSourceTypeIcon(doc.sourceType);
                const sourceLabel = aggregatedDocumentsService.getSourceTypeLabel(doc.sourceType);
                const uploadedAtParsed = parseDbTimestamp(doc.uploadedAt);
                const time = uploadedAtParsed ? format(uploadedAtParsed, 'HH:mm', { locale: ptBR }) : '-';

                return (
                  <div
                    key={doc.id}
                    className={`relative rounded-lg border bg-card p-3 transition-all sm:p-4 ${
                      isSelected
                        ? 'border-primary shadow-sm'
                        : 'border-border hover:border-input hover:shadow-sm'
                    }`}
                  >
                    {/* Timeline dot */}
                    <div className="absolute -left-[27px] top-6 size-3 rounded-full border-2 border-background bg-primary sm:-left-[39px]"></div>

                    <div className="flex items-start gap-3 sm:gap-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(doc.id)}
                        className="mt-1 size-4 rounded-sm border-input accent-primary"
                      />

                      <div className="shrink-0">
                        <span className="text-2xl sm:text-3xl">{icon}</span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex min-w-0 items-center justify-between gap-2">
                          <button
                            onClick={() => onPreview(doc)}
                            className="truncate text-base font-semibold hover:text-primary"
                          >
                            {doc.fileName}
                          </button>
                          <span className="shrink-0 text-sm text-muted-foreground">
                            {time}
                          </span>
                        </div>
                        <DocumentDescription name={doc.fileName} description={doc.description} onSave={getDescriptionSaver?.(doc)} />

                        <div className="mt-2 flex items-center flex-wrap gap-2 text-sm text-muted-foreground">
                          <span className="inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-secondary-foreground">
                            <span className="mr-1">{sourceIcon}</span>
                            {sourceLabel}
                          </span>

                          <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-foreground">
                            {doc.documentType}
                          </span>

                          <span className="text-muted-foreground">
                            {size}
                          </span>

                          {doc.isConfidential && (
                            <span className="inline-flex items-center rounded-full border border-destructive/30 px-2.5 py-0.5 text-destructive">
                              <Lock className="h-3 w-3 mr-1" />
                              Confidencial
                            </span>
                          )}
                        </div>

                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                          <div className="text-sm text-muted-foreground">
                            {doc.sourceEntityName}
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              por {doc.uploadedByName}
                            </span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => onDownload(doc)}
                                  className="rounded-sm p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                  aria-label="Baixar"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Baixar</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
